import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./CustomSelect.css";

function useDropdownPos(triggerRef, open) {
  const [pos, setPos] = useState(null);

  const update = useCallback(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [open, triggerRef]);

  useEffect(() => { update(); }, [update]);

  return pos;
}

export default function CustomSelect({ value, options, onChange, disabled, placeholder, multi, renderOption }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const pos = useDropdownPos(triggerRef, open);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The portal is position:fixed with a once-computed position — page scrolling
  // would leave it floating detached from the trigger. Close instead (scrolling
  // INSIDE the list is fine and must not close it). Escape closes too.
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      if (dropdownRef.current && e.target instanceof Node && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Multi-select: value is an array of selected values
  const selectedSet = multi ? new Set(value || []) : null;

  const displayLabel = (() => {
    if (multi) {
      if (!value || value.length === 0) return placeholder || "Alle";
      if (value.length === 1) {
        const match = options.find((o) => (typeof o === "string" ? o : String(o.value)) === String(value[0]));
        return match ? (typeof match === "string" ? match : match.label) : value[0];
      }
      return `${value.length} ausgewählt`;
    }
    if (!value && value !== 0 && placeholder) return placeholder;
    const match = options.find((o) => (typeof o === "string" ? o : String(o.value)) === String(value));
    if (!match) return value || placeholder || "";
    return typeof match === "string" ? match : match.label;
  })();

  const handleOptionClick = (val) => {
    if (multi) {
      const next = new Set(selectedSet);
      if (next.has(val)) {
        next.delete(val);
      } else {
        next.add(val);
      }
      onChange([...next]);
    } else {
      onChange(val);
      setOpen(false);
    }
  };

  return (
    <div className={`custom-select ${disabled ? "disabled" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`custom-select-trigger ${open ? "open" : ""} ${!multi && !value && value !== 0 && placeholder ? "placeholder" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <span>{renderOption && !multi && value != null
          ? renderOption(options.find((o) => (typeof o === "string" ? o : String(o.value)) === String(value)) || { label: displayLabel })
          : displayLabel}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="custom-select-dropdown"
          style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: pos.width }}
        >
          {multi && (
            <button
              type="button"
              className={`custom-select-option ${selectedSet.size === 0 ? "active" : ""}`}
              onClick={() => { onChange([]); }}
            >
              Alle
            </button>
          )}
          {options.map((opt) => {
            const val = typeof opt === "string" ? opt : opt.value;
            const label = typeof opt === "string" ? opt : opt.label;
            const isActive = multi ? selectedSet.has(val) : String(val) === String(value);
            return (
              <button
                key={val}
                type="button"
                className={`custom-select-option ${isActive ? "active" : ""}`}
                onClick={() => handleOptionClick(val)}
              >
                {multi && (
                  <span className={`multi-check ${isActive ? "checked" : ""}`}>
                    {isActive && <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none"/></svg>}
                  </span>
                )}
                {renderOption ? renderOption(opt) : label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
